import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { environment } from '../../../../environments/environment';
import { ProductFiltersButton } from '../../../components/product/product-filters-button/product-filters-button';
import {
  ProductFilters,
  ProductFiltersPanel,
} from '../../../components/product/product-filters-panel/product-filters-panel';
import { ProductList } from '../../../components/product/product-list/product-list';
import { ProductOrderBySelect } from '../../../components/product/product-order-by-select/product-order-by-select';
import { RecentlyViewedProductList } from '../../../components/product/recently-viewed-product-list/recently-viewed-product-list';
import { Product } from '../../../models/product';
import {
  ProductApiService,
  ProductOrderBy,
} from '../../../services/apis/product-api.service';
import { MessageService } from '../../../services/message.service';
import { TitleService } from '../../../services/title.service';
import { mergeRouter } from '../../../utils/router';

@Component({
  selector: 'app-product-search-page',
  imports: [
    RouterModule,
    MatProgressSpinnerModule,
    ProductFiltersPanel,
    FormsModule,
    MatProgressSpinnerModule,
    MatPaginatorModule,
    ProductOrderBySelect,
    ProductFiltersButton,
    RecentlyViewedProductList,
    NzEmptyModule,
    ProductList,
  ],
  templateUrl: './product-search-page.html',
  styleUrl: './product-search-page.scss',
})
export class ProductSearchPage {
  query: string = '';
  filters: ProductFilters = {
    sizes: [],
    color: '',
    category_ids: [],
    tags: []
  };
  productsLoading: boolean = false;
  page: number = 1;
  pageSize: number = 24;
  total: number = 0;
  products: Product[] = [];
  orderBy: ProductOrderBy = ProductOrderBy.RECOMMENDED;
  constructor(
    private titleService: TitleService,
    private productApi: ProductApiService,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService
  ) {
    this.titleService.setTitle('Search');
  }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((params) => {
      this.query = params.get('q') ?? '';
      this.filters.sizes = params.getAll('sizes');
      this.filters.color = params.get('color') ?? '';
      this.filters.category_ids = params
        .getAll('category_ids')
        .map((v) => parseInt(v));
      this.filters.tags = params.getAll('tags');
      this.page = parseInt(params.get('page') ?? '1');
      this.orderBy =
        (params.get('order_by') as ProductOrderBy) ??
        ProductOrderBy.RECOMMENDED;
      this.findProducts();
    });
  }

  onFiltersChanged() {
    this.page = 1;
    mergeRouter(this.router, this.route, {
      sizes: this.filters.sizes,
      color: this.filters.color,
      category_ids: this.filters.category_ids,
      tags: this.filters.tags,
      page: this.page,
    });
  }

  onOrderByChanged() {
    this.page = 1;
    mergeRouter(this.router, this.route, {
      order_by: this.orderBy,
      page: this.page,
    });
  }

  async findProducts() {
    try {
      this.productsLoading = true;
      const r = await this.productApi.findProducts({
        query: this.query,
        page: this.page,
        page_size: this.pageSize,
        color: this.filters.color,
        sizes: this.filters.sizes,
        category_ids: this.filters.category_ids,
        order_by: this.orderBy,
        tags: this.filters.tags,
      });
      this.products = r.items;
      this.total = r.total;
      this.page = r.page;
      this.pageSize = r.page_size;
      this.setSearchSeo();
    } catch (error) {
    } finally {
      this.productsLoading = false;
    }
  }

  onPageChanged(event: PageEvent) {
    this.page = event.pageIndex + 1;
    mergeRouter(this.router, this.route, {
      page: this.page,
    });
  }

  private setSearchSeo() {
    const queryText = this.query ? ` for "${this.query}"` : '';

    this.titleService.setPageMeta({
      title: `Search Results${queryText}`,
      canonicalPath: '/search',
      description: `${this.total} ${environment.appname} products found${queryText}. Refine by color, size, category, and latest arrivals.`,
      robots: 'noindex, follow',
      keywords: [
        'search',
        this.query,
        "women's activewear",
        'yoga clothes',
        'workout clothes',
        'sports bras',
        'leggings',
        'workout sets',
      ].filter((keyword) => keyword.length > 0),
    });
  }
}
